import type { CSSProperties } from 'react'

import { cn } from '@/lib/utils'

type GlowVariant = 'hero' | 'cta' | 'card'

type GlowProps = {
    variant?: GlowVariant
    className?: string
}

type LayerStyle = CSSProperties & {
    '--glow-blur': string
    '--glow-duration': string
    '--glow-direction'?: string
}

type Layer = {
    /** Square edge length in pixels. */
    size: number
    blur: number
    gradient: string
    opacity?: number
    blend?: boolean
    duration: number
    reverse?: boolean
}

/**
 * Every glow is built from two conic gradients. `vivid` carries the main colour
 * sweep; `soft` is layered over it twice, the second time in `overlay`, which is
 * what produces the bright core and the contrasting edge.
 *
 * The stop angles are inherited from the original and are deliberately uneven —
 * they are what makes the rotation feel organic rather than mechanical, so only
 * the colours change between palettes.
 *
 * To switch the site's glow, change ACTIVE_PALETTE below. Nothing else moves.
 */
const PALETTES = {
    /**
     * Sunset into nightfall. Anchored on the brand red, through orange and gold,
     * into pink and violet. Keeps the original's warm/cool contrast — which is
     * what gives the bloom depth — without its primary-blue harshness.
     */
    aurora: {
        vivid: 'conic-gradient(#ff2d16 0deg, #ff4533 54.8916deg, #ff8a3d 106.699deg, #ffc76b 162deg, #ff5c8a 252deg, #7b3dff 306deg, #ff2d16 360deg)',
        soft: 'conic-gradient(#fff2de 0deg, #6d4dff 180deg, #ffb46b 360deg)'
    },
    ember: {
        vivid: 'conic-gradient(#e01608 0deg, #ff2d16 54.8916deg, #ff4533 106.699deg, #ff8a2b 162deg, #ffc247 252deg, #ff6a12 306deg, #e01608 360deg)',
        soft: 'conic-gradient(#fff1d6 0deg, #ff7a2f 180deg, #ffcf8a 360deg)'
    },
    violet: {
        vivid: 'conic-gradient(#4c1d95 0deg, #7c3aed 54.8916deg, #a855f7 106.699deg, #e879f9 162deg, #ff4ecd 252deg, #6d28d9 306deg, #4c1d95 360deg)',
        soft: 'conic-gradient(#f5d0fe 0deg, #7c3aed 180deg, #ff8ae2 360deg)'
    },
    teal: {
        vivid: 'conic-gradient(#042f2e 0deg, #0d9488 54.8916deg, #14b8a6 106.699deg, #2dd4bf 162deg, #67e8f9 252deg, #0891b2 306deg, #042f2e 360deg)',
        soft: 'conic-gradient(#ccfbf1 0deg, #0891b2 180deg, #5eead4 360deg)'
    },
    gold: {
        vivid: 'conic-gradient(#78350f 0deg, #b45309 54.8916deg, #f59e0b 106.699deg, #fcd34d 162deg, #1d4ed8 252deg, #1e3a8a 306deg, #78350f 360deg)',
        soft: 'conic-gradient(#fef3c7 0deg, #1d4ed8 180deg, #fbbf24 360deg)'
    }
} as const

const ACTIVE_PALETTE: keyof typeof PALETTES = 'aurora'

export const VIVID_CONIC = PALETTES[ACTIVE_PALETTE].vivid
export const SOFT_CONIC = PALETTES[ACTIVE_PALETTE].soft

const HERO_LAYERS: Layer[] = [
    { size: 330, blur: 50, gradient: VIVID_CONIC, duration: 28 },
    { size: 235, blur: 26, gradient: SOFT_CONIC, duration: 20, reverse: true },
    { size: 235, blur: 26, gradient: SOFT_CONIC, duration: 14, blend: true }
]

const CTA_LAYERS: Layer[] = [
    { size: 400, blur: 70, gradient: VIVID_CONIC, opacity: 0.8, duration: 30 },
    { size: 300, blur: 32, gradient: SOFT_CONIC, duration: 22, reverse: true },
    { size: 300, blur: 32, gradient: SOFT_CONIC, duration: 15, blend: true }
]

/**
 * Stage geometry, straight from the original, with one deliberate change.
 *
 * `hero` is 1200x800 hanging 400px below the hero section, so its centre lands
 * exactly on the section's bottom edge. The section's own `overflow: hidden`
 * slices the lower half off, which is what gives the bloom its flat bottom.
 *
 * `cta` is deliberately un-clipped so it spills past the section and lights up
 * the top of the translucent footer. Its centre sits on the section's own
 * bottom edge (the boundary with the footer), so the bloom reads as an even
 * half-and-half split: one half inside the CTA section clear of the card
 * above it, the other half glowing through the top of the footer below.
 */
const STAGES: Record<GlowVariant, string> = {
    hero: 'bottom-[-400px] left-1/2 h-[800px] w-[1200px] -translate-x-1/2 overflow-hidden',
    cta: 'bottom-[-317px] left-0 h-[634px] w-full overflow-visible lg:left-1/2 lg:w-[951px] lg:-translate-x-1/2',
    /** Hangs off a card corner; callers override which corner. */
    card: 'top-[-400px] left-[-260px] h-[500px] w-[500px] overflow-visible'
}

const LAYERS: Record<GlowVariant, Layer[]> = {
    hero: HERO_LAYERS,
    cta: CTA_LAYERS,
    card: HERO_LAYERS
}

export const Glow = ({ variant = 'hero', className }: GlowProps) => {
    return (
        <div
            aria-hidden='true'
            className={cn('pointer-events-none absolute z-0', STAGES[variant], className)}
        >
            {LAYERS[variant].map((layer, index) => {
                const layerStyle: LayerStyle = {
                    '--glow-blur': `${layer.blur}px`,
                    '--glow-duration': `${layer.duration}s`,
                    width: layer.size,
                    height: layer.size,
                    opacity: layer.opacity,
                    mixBlendMode: layer.blend ? 'overlay' : undefined
                }

                const conicStyle: CSSProperties = {
                    backgroundImage: layer.gradient,
                    animationDirection: layer.reverse ? 'reverse' : undefined
                }

                return (
                    <div
                        key={index}
                        className='glow-layer'
                        style={layerStyle}
                    >
                        <div
                            className='glow-conic'
                            style={conicStyle}
                        />
                    </div>
                )
            })}
        </div>
    )
}
